'use client'

/**
 * Gedeelde document-acties (STREEP): Preview + directe Download (+ optioneel XML).
 *
 * Eén component, overal hergebruikt (werkbon-, factuur-, document-detail en
 * bijlagen). Vervangt de kale `<a href={pdfUrl}>`-links die het token niet
 * meedroegen en het bestand in een nieuw venster openden.
 *
 *   - Preview  -> opent de `PdfPreviewModal` (inline viewer, geen download).
 *   - Download -> geauthenticeerde blob-fetch + programmatic `<a download>`:
 *                 één klik = direct het bestand op schijf, geen tussenvenster.
 *   - XML      -> idem als Download, voor de XML-export (indien `xmlUrl`).
 *
 * Alle bron-URL's zijn same-origin proxy-routes (de Auth.js-sessiecookie reist
 * mee; de proxy zet de Bearer-token server-side). De endpoints checken zelf de
 * rechten (`canView`); deze component toont alleen knoppen.
 */

import { useCallback, useState } from 'react'
import { Download, Eye, FileCode2, Loader2 } from 'lucide-react'
import { cn } from '../lib/utils'
import { downloadDocument } from '../lib/document-blob'
import { PdfPreviewModal } from './pdf-preview-modal'

type Size = 'sm' | 'md'

export interface DocumentActionsProps {
  /** Same-origin proxy-URL die het PDF/document levert. */
  url: string
  /**
   * Optionele tweede preview-bron (same-origin proxy-URL), doorgegeven aan de
   * viewer. Lukt `url` niet, dan valt de preview hierop terug (bv. de bron-PDF
   * van een geimporteerde factuur).
   */
  fallbackUrl?: string
  /** Bestandsnaam voor de download + viewer-titel. */
  fileName: string
  /** Optionele mime-hint (anders uit Content-Type/bestandsnaam afgeleid). */
  mimeType?: string
  /** Optioneel label op de download-knop (default "Download"). */
  downloadLabel?: string
  /** Optionele XML-export (same-origin proxy-URL) + bestandsnaam. */
  xmlUrl?: string
  xmlFileName?: string
  /** Toon de preview-knop (default true). */
  showPreview?: boolean
  /** Compacte (icon-only) variant voor in tabellen/rijen. */
  iconOnly?: boolean
  size?: Size
  className?: string
  /**
   * Permanente rotatie opslaan (alleen documenten met canEdit). Doorgegeven aan
   * de viewer; afwezig -> alleen tijdelijke (CSS) rotatie in de preview.
   */
  onRotatePermanent?: (rotationDegrees: number) => Promise<{ ok: boolean; message?: string }>
  /** Aangeroepen na een geslaagde permanente rotatie (bv. router.refresh). */
  onRotated?: () => void
  /** Foutmelding doorgeven aan de app (bv. toast). Afwezig -> inline tonen. */
  onError?: (message: string) => void
}

const DOWNLOAD_ERROR = 'Downloaden mislukt - probeer opnieuw.'

export function DocumentActions({
  url,
  fallbackUrl,
  fileName,
  mimeType,
  downloadLabel,
  xmlUrl,
  xmlFileName,
  showPreview = true,
  iconOnly = false,
  size = 'sm',
  className,
  onRotatePermanent,
  onRotated,
  onError,
}: DocumentActionsProps) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadingXml, setDownloadingXml] = useState(false)
  const [inlineError, setInlineError] = useState<string | null>(null)

  const reportError = useCallback(
    (message: string) => {
      if (onError) onError(message)
      else setInlineError(message)
    },
    [onError],
  )

  const handleDownload = useCallback(async () => {
    setDownloading(true)
    setInlineError(null)
    try {
      await downloadDocument(url, fileName, { mimeType })
    } catch {
      reportError(DOWNLOAD_ERROR)
    } finally {
      setDownloading(false)
    }
  }, [url, fileName, mimeType, reportError])

  const handleDownloadXml = useCallback(async () => {
    if (!xmlUrl) return
    setDownloadingXml(true)
    setInlineError(null)
    try {
      await downloadDocument(xmlUrl, xmlFileName ?? `${fileName}.xml`, { mimeType: 'application/xml' })
    } catch {
      reportError(DOWNLOAD_ERROR)
    } finally {
      setDownloadingXml(false)
    }
  }, [xmlUrl, xmlFileName, fileName, reportError])

  // size="md" => 44px hoog (portal tap-target ≥ 44px); size="sm" => compact (backoffice).
  const btn = cn(
    'inline-flex items-center justify-center gap-2 rounded-lg border border-[#CED0C8] bg-white font-medium text-[#1A1A18] text-sm transition-colors hover:bg-[#F0F1EC] disabled:opacity-50',
    size === 'md' && 'min-h-[44px]',
    iconOnly
      ? size === 'md'
        ? 'min-w-[44px] px-2'
        : 'px-2 py-2'
      : size === 'md'
        ? 'px-4'
        : 'px-3 py-1.5',
  )
  const iconCls = 'h-4 w-4'

  return (
    <span className={cn('inline-flex flex-wrap items-center gap-2', className)}>
      {showPreview && (
        <button type="button" className={btn} onClick={() => setPreviewOpen(true)} aria-label={`${fileName} bekijken`}>
          <Eye className={iconCls} aria-hidden="true" />
          {!iconOnly && 'Bekijken'}
        </button>
      )}

      <button
        type="button"
        className={btn}
        onClick={handleDownload}
        disabled={downloading}
        aria-label={`${fileName} downloaden`}
      >
        {downloading ? (
          <Loader2 className={cn(iconCls, 'animate-spin')} aria-hidden="true" />
        ) : (
          <Download className={iconCls} aria-hidden="true" />
        )}
        {!iconOnly && (downloadLabel ?? 'Download')}
      </button>

      {xmlUrl && (
        <button
          type="button"
          className={btn}
          onClick={handleDownloadXml}
          disabled={downloadingXml}
          aria-label="XML downloaden"
        >
          {downloadingXml ? (
            <Loader2 className={cn(iconCls, 'animate-spin')} aria-hidden="true" />
          ) : (
            <FileCode2 className={iconCls} aria-hidden="true" />
          )}
          {!iconOnly && 'XML'}
        </button>
      )}

      {inlineError ? (
        <span role="alert" className="text-xs font-medium text-[#9B2C2C]">
          {inlineError}
        </span>
      ) : null}

      {previewOpen && (
        <PdfPreviewModal
          url={url}
          fallbackUrl={fallbackUrl}
          fileName={fileName}
          mimeType={mimeType}
          onClose={() => setPreviewOpen(false)}
          onRotatePermanent={onRotatePermanent}
          onRotated={onRotated}
        />
      )}
    </span>
  )
}
